export interface GridControlPoint {
  readonly easting: string;
  readonly northing: string;
  readonly x: number;
  readonly y: number;
}

export interface GridProjection {
  readonly easting: string;
  readonly northing: string;
  readonly value: string;
}

export interface TraceGridTransform {
  readonly controlPrecision: number;
  readonly outputPrecision: number;
  project(x: number, y: number): GridProjection | null;
}

export type TraceGridTransformResult =
  | { readonly ok: true; readonly transform: TraceGridTransform }
  | {
      readonly ok: false;
      readonly code:
        | "CONTROL_COUNT"
        | "DEGENERATE_CONTROLS"
        | "INVALID_COORDINATE"
        | "INVALID_OUTPUT_PRECISION"
        | "MIXED_PRECISION";
    };

const CONTROL_COUNT = 4;
const COEFFICIENT_COUNT = 8;
const MINIMUM_PRECISION = 2;
const MAXIMUM_PRECISION = 5;
const SINGULAR_TOLERANCE = 1e-10;

function validCoordinate(value: string): boolean {
  return /^\d{2,5}$/.test(value);
}

function requiredValue<T>(value: T | undefined): T {
  /* v8 ignore next -- callers index fixed-size internal matrices only */
  if (value === undefined) throw new Error("Invalid internal matrix shape");
  return value;
}

function solveLinearSystem(
  sourceRows: readonly (readonly number[])[],
  sourceValues: readonly number[],
): readonly number[] | null {
  const rows = sourceRows.map((row, index) => [
    ...row,
    requiredValue(sourceValues[index]),
  ]);

  for (let pivot = 0; pivot < COEFFICIENT_COUNT; pivot += 1) {
    let strongest = pivot;
    for (let row = pivot + 1; row < COEFFICIENT_COUNT; row += 1) {
      if (
        Math.abs(requiredValue(requiredValue(rows[row])[pivot])) >
        Math.abs(requiredValue(requiredValue(rows[strongest])[pivot]))
      ) {
        strongest = row;
      }
    }
    const pivotRow = requiredValue(rows[strongest]);
    if (Math.abs(requiredValue(pivotRow[pivot])) < SINGULAR_TOLERANCE) {
      return null;
    }
    [rows[pivot], rows[strongest]] = [pivotRow, requiredValue(rows[pivot])];
    const active = requiredValue(rows[pivot]);
    const divisor = requiredValue(active[pivot]);
    for (let column = pivot; column <= COEFFICIENT_COUNT; column += 1) {
      active[column] = requiredValue(active[column]) / divisor;
    }
    for (let row = 0; row < COEFFICIENT_COUNT; row += 1) {
      if (row === pivot) continue;
      const other = requiredValue(rows[row]);
      const factor = requiredValue(other[pivot]);
      for (let column = pivot; column <= COEFFICIENT_COUNT; column += 1) {
        other[column] =
          requiredValue(other[column]) - factor * requiredValue(active[column]);
      }
    }
  }

  return rows.map((row) => requiredValue(row[COEFFICIENT_COUNT]));
}

function homographyRows(controls: readonly GridControlPoint[]): {
  readonly rows: readonly (readonly number[])[];
  readonly values: readonly number[];
} {
  const rows: number[][] = [];
  const values: number[] = [];
  for (const { easting, northing, x, y } of controls) {
    const east = Number(easting);
    const north = Number(northing);
    rows.push([x, y, 1, 0, 0, 0, -east * x, -east * y]);
    values.push(east);
    rows.push([0, 0, 0, x, y, 1, -north * x, -north * y]);
    values.push(north);
  }
  return { rows, values };
}

export function createTraceGridTransform(
  controls: readonly GridControlPoint[],
  outputPrecision: number,
): TraceGridTransformResult {
  if (controls.length !== CONTROL_COUNT)
    return { ok: false, code: "CONTROL_COUNT" };
  if (
    !Number.isInteger(outputPrecision) ||
    outputPrecision < MINIMUM_PRECISION ||
    outputPrecision > MAXIMUM_PRECISION
  ) {
    return { ok: false, code: "INVALID_OUTPUT_PRECISION" };
  }
  if (
    controls.some(
      ({ easting, northing, x, y }) =>
        !validCoordinate(easting) ||
        !validCoordinate(northing) ||
        !Number.isFinite(x) ||
        !Number.isFinite(y) ||
        x < 0 ||
        x > 1 ||
        y < 0 ||
        y > 1,
    )
  ) {
    return { ok: false, code: "INVALID_COORDINATE" };
  }

  const precisions = new Set(
    controls.flatMap(({ easting, northing }) => [
      easting.length,
      northing.length,
    ]),
  );
  if (precisions.size !== 1) return { ok: false, code: "MIXED_PRECISION" };
  const controlPrecision = requiredValue(controls[0]).easting.length;

  const { rows, values } = homographyRows(controls);
  const coefficients = solveLinearSystem(rows, values);
  if (coefficients === null) return { ok: false, code: "DEGENERATE_CONTROLS" };
  const [a, b, c, d, e, f, g, h] = coefficients as [
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
  ];

  const outputScale = 10 ** (outputPrecision - controlPrecision);
  const maximum = 10 ** outputPrecision - 1;
  const formatAxis = (value: number): string | null => {
    const projected = Math.round(value * outputScale);
    return projected < 0 || projected > maximum
      ? null
      : String(projected).padStart(outputPrecision, "0");
  };

  return {
    ok: true,
    transform: {
      controlPrecision,
      outputPrecision,
      project: (x, y) => {
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
        const denominator = g * x + h * y + 1;
        if (Math.abs(denominator) < SINGULAR_TOLERANCE) return null;
        const projectedEasting = formatAxis((a * x + b * y + c) / denominator);
        const projectedNorthing = formatAxis((d * x + e * y + f) / denominator);
        return projectedEasting === null || projectedNorthing === null
          ? null
          : {
              easting: projectedEasting,
              northing: projectedNorthing,
              value: `${projectedEasting} ${projectedNorthing}`,
            };
      },
    },
  };
}
